/**
 * Uplifted from `commonComponents/LeftMenu/menuItemsData.ts`, seeded with
 * `LICENSE_LANDING_ROUTE` — the licence-code → landing-route map that
 * `helpers/commonHelpers.redirectToLicenseLandingPage` looks up — and with `LICENCE_CODES`
 * itself, which `helpers/catalogueHelpers.fetchCatalogBasedOnLicence` gates on.
 *
 * ⚠️ **This is the first `commonComponents/` folder in core, and it holds NO UI.** The folder
 * name mirrors the SOURCE FILE (CLAUDE.md "Uplifting a module's dependencies"), and that source
 * happens to sit under a UI directory; what moved is a plain data map with no React in it. Core
 * still imports nothing from `@rista/ui` and must not start here — the rest of `menuItemsData`
 * (nav items, icons, `checkCashierRestrictField`) stays in the apps.
 *
 * ⚠️ **The two maps are NOT interchangeable** — the values come from each platform's own route
 * vocabulary, so web's are PATHS (`"/new-order"`) and mobile's are page IDS (`"new-order"`).
 * See `utils/routeConstants/shared.ts`. That is why there is no shared body here.
 *
 * The KEYS are identical on both sides, and are `LICENCE_CODES`: INV, MKT, ALT, SEP, SLT, SLF,
 * SOL, POO, SPT, POS, RHP, KSK. `POD` was removed upstream on both platforms; `KSK` was too, and
 * is back now that the kiosk is its own licence module (it lands on the kiosk's Home page — a
 * merchant-panel page, never the kiosk itself: a login must not put a device into kiosk mode).
 */

/** The licence codes both platforms map. Documented so a missing key is visible as a gap. */
export type LicenceCode =
  | "INV"
  | "MKT"
  | "ALT"
  | "SEP"
  | "SLT"
  | "SLF"
  | "SOL"
  | "POO"
  | "SPT"
  | "POS"
  | "RHP"
  | "KSK";

/**
 * The licence codes themselves. **Byte-identical in both apps** (diffed, not assumed), so unlike
 * `LICENSE_LANDING_ROUTE` — whose VALUES are each platform's own route vocabulary — this one has
 * a single shared definition.
 *
 * ⚠️ Carries one key `LICENSE_LANDING_ROUTE` does not map, `POD`. That asymmetry is the apps'
 * own and is preserved: a code can exist without a landing route.
 */
export const LICENCE_CODES = {
  INV: 'INV',
  MKT: 'MKT',
  ALT: 'ALT',
  SEP: 'SEP',
  SPT: 'SPT',
  POS: 'POS',
  SLT: 'SLT',
  SLF: 'SLF',
  SOL: 'SOL',
  POO: 'POO',
  RHP: 'RHP',
  POD: 'POD',
  KSK: 'KSK',
};
